
import { ThumbsUp, ThumbsDown, Copy, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface MessageActionsProps {
  messageId: string;
  messageContent: string;
  audioUrl?: string;
  feedbackGiven: "like" | "dislike" | null;
  isPlaying: string | null;
  onFeedback: (type: "like" | "dislike") => void;
  onToggleAudio: () => void;
}

export function MessageActions({
  messageId,
  messageContent,
  audioUrl,
  feedbackGiven,
  isPlaying,
  onFeedback,
  onToggleAudio
}: MessageActionsProps) {
  const copyMessageToClipboard = () => {
    navigator.clipboard.writeText(messageContent);
    toast({
      title: "Copied to clipboard",
      description: "Message content has been copied to your clipboard.",
    });
  };

  const handleFeedback = (type: "like" | "dislike") => {
    onFeedback(type);
    toast({
      title: type === "like" ? "Thanks for your feedback!" : "Thanks for your feedback",
      description: type === "like" 
        ? "We're glad this was helpful." 
        : "We'll use this to improve our responses.",
    });
  };

  return (
    <div className="mt-2 flex items-center justify-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      <div className="bg-background/80 backdrop-blur-sm border rounded-full p-1 flex items-center gap-1 shadow-sm">
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-6 w-6 rounded-full",
            feedbackGiven === "like" && "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
          )}
          onClick={() => handleFeedback("like")}
          disabled={feedbackGiven !== null}
        >
          <ThumbsUp className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-6 w-6 rounded-full",
            feedbackGiven === "dislike" && "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
          )}
          onClick={() => handleFeedback("dislike")}
          disabled={feedbackGiven !== null}
        >
          <ThumbsDown className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-6 w-6 rounded-full",
            isPlaying === messageId && "bg-primary/10 text-primary"
          )}
          onClick={onToggleAudio}
        >
          {isPlaying === messageId ? (
            <VolumeX className="h-3 w-3" />
          ) : (
            <Volume2 className="h-3 w-3" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 rounded-full"
          onClick={copyMessageToClipboard}
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
